<script setup lang="ts">
import { IconCheck } from '@tabler/icons-vue';
import { computed, ref, watch } from 'vue';

import BypassConfirmationModal from './BypassConfirmationModal.vue';

export interface ApprovalData {
  customerEvidenceFiles: File[];
  hasPhoneChange: boolean;
  newPhoneNumber?: string;
  notes?: string;
  zendeskTicket: string;
}

interface Emits {
  (_e: 'approve', _data: ApprovalData): void;
  (_e: 'update:modelValue', _value: boolean): void;
}

interface Props {
  errorMessage?: string;
  loading?: boolean;
  modelValue: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  errorMessage: '',
  loading: false,
});

const emit = defineEmits<Emits>();

const customerEvidenceFiles = ref<File[]>([]);
const hasPhoneChange = ref(false);
const newPhoneNumber = ref('');
const zendeskTicket = ref('');
const notes = ref('');
const showErrors = ref(false);

const showConfirmModal = ref(false);
const pendingApprovalData = ref<ApprovalData | null>(null);

const customerEvidenceInput = ref<HTMLInputElement | null>(null);

const hasCustomerEvidence = computed(() => customerEvidenceFiles.value.length > 0);

const canApprove = computed(() => {
  if (!hasCustomerEvidence.value) return false;
  if (hasPhoneChange.value && !newPhoneNumber.value) return false;
  return true;
});

watch(hasPhoneChange, (newValue) => {
  if (!newValue) {
    newPhoneNumber.value = '';
  }
});

const handleCustomerEvidenceUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = Array.from(target.files || []);
  customerEvidenceFiles.value.push(...files);
};

const removeCustomerEvidence = (index: number) => {
  customerEvidenceFiles.value.splice(index, 1);
  if (customerEvidenceInput.value) {
    customerEvidenceInput.value.value = '';
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / (k ** i)) * 100) / 100} ${sizes[i]}`;
};

const handleClose = () => {
  if (props.loading) return;
  showErrors.value = false;
  emit('update:modelValue', false);
};

const handleApprove = () => {
  if (!canApprove.value) {
    showErrors.value = true;
    return;
  }

  pendingApprovalData.value = {
    customerEvidenceFiles: customerEvidenceFiles.value,
    hasPhoneChange: hasPhoneChange.value,
    newPhoneNumber: newPhoneNumber.value || undefined,
    notes: notes.value || undefined,
    zendeskTicket: zendeskTicket.value,
  };

  showConfirmModal.value = true;
};

const handleConfirmApproval = () => {
  if (pendingApprovalData.value) {
    emit('approve', pendingApprovalData.value);
    showConfirmModal.value = false;
    pendingApprovalData.value = null;
  }
};

const handleCancelConfirmation = () => {
  showConfirmModal.value = false;
  pendingApprovalData.value = null;
};

watch(() => props.modelValue, (newValue) => {
  if (!newValue) {
    customerEvidenceFiles.value = [];
    hasPhoneChange.value = false;
    newPhoneNumber.value = '';
    zendeskTicket.value = '';
    notes.value = '';
    showErrors.value = false;

    if (customerEvidenceInput.value) {
      customerEvidenceInput.value.value = '';
    }
  }
});
</script>

<template>
  <Teleport v-if="modelValue" to="body">
    <div
      class="modal modal-blur fade show"
      style="display: block"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Aprovação Manual de Instalação</h5>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              :disabled="loading"
              @click="handleClose"
            />
          </div>

          <div class="modal-body">
            <div v-if="errorMessage" class="alert alert-danger mb-4">
              <div class="alert-with-icon">
                <i class="icon ti ti-alert-circle" />
                <div class="content">
                  <h5 class="heading">Erro ao Aprovar Instalação</h5>
                  <p class="mb-0">{{ errorMessage }}</p>
                </div>
              </div>
            </div>

            <div class="alert alert-info mb-4">
              <div class="alert-with-icon">
                <i class="icon ti ti-info-circle" />
                <div class="content">
                  <p class="mb-0">
                    Esta aprovação manual será registrada e auditada.
                    Certifique-se de anexar todas as evidências necessárias.
                  </p>
                </div>
              </div>
            </div>

            <div class="mb-4">
              <label class="form-label">
                Evidência do Aceite do Cliente
                <span class="text-danger">*</span>
              </label>
              <input
                id="customerEvidenceInput"
                ref="customerEvidenceInput"
                type="file"
                class="form-control"
                :class="{ 'is-invalid': showErrors && !hasCustomerEvidence }"
                accept="*/*"
                multiple
                :disabled="loading"
                @change="handleCustomerEvidenceUpload"
              />
              <small class="form-hint">
                Anexe prints, fotos ou documentos que comprovem o aceite do cliente.
                Múltiplos arquivos permitidos.
              </small>
              <div v-if="showErrors && !hasCustomerEvidence" class="invalid-feedback d-block">
                É obrigatório anexar ao menos uma evidência do aceite do cliente.
              </div>

              <div v-if="customerEvidenceFiles.length > 0" class="mt-3">
                <div class="list-group">
                  <div
                    v-for="(file, index) in customerEvidenceFiles"
                    :key="index"
                    class="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <i class="icon ti ti-file" />
                      <div>
                        <div class="fw-medium">{{ file.name }}</div>
                        <small class="text-muted">{{ formatFileSize(file.size) }}</small>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm btn-ghost-danger"
                      :disabled="loading"
                      @click="removeCustomerEvidence(index)"
                    >
                      <i class="icon ti ti-x" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-4">
              <label class="form-check">
                <input
                  v-model="hasPhoneChange"
                  type="checkbox"
                  class="form-check-input"
                  :disabled="loading"
                />
                <span class="form-check-label">Tem troca de telefone?</span>
              </label>
            </div>

            <div v-if="hasPhoneChange" class="mb-4">
              <label class="form-label" for="newPhoneNumber">
                Novo número de telefone
                <span class="text-danger">*</span>
              </label>
              <input
                id="newPhoneNumber"
                v-model="newPhoneNumber"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': showErrors && hasPhoneChange && !newPhoneNumber }"
                placeholder="Ex: +55 11 99999-9999"
                :disabled="loading"
              />
              <small class="form-hint">
                Informe o novo número de telefone do cliente para registro.
              </small>
              <div v-if="showErrors && hasPhoneChange && !newPhoneNumber" class="invalid-feedback d-block">
                É obrigatório informar o novo número de telefone quando há troca de telefone.
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label" for="zendeskTicket">
                Ticket Zendesk (opcional)
              </label>
              <input
                id="zendeskTicket"
                v-model="zendeskTicket"
                type="text"
                class="form-control"
                placeholder="Ex: #12345"
                :disabled="loading"
              />
              <small class="form-hint">
                Caso exista um ticket relacionado, informe o número para referência.
              </small>
            </div>

            <div class="mb-3">
              <label class="form-label" for="notes">
                Observações adicionais (opcional)
              </label>
              <textarea
                id="notes"
                v-model="notes"
                class="form-control"
                rows="3"
                placeholder="Adicione observações ou informações relevantes sobre a aprovação..."
                :disabled="loading"
              />
              <small class="form-hint">
                Use este campo para adicionar contexto ou justificativas adicionais.
              </small>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="loading"
              @click="handleClose"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-success"
              :disabled="!canApprove || loading"
              @click="handleApprove"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" />
              <IconCheck v-else :size="20" class="me-2" />
              Aprovar Instalação
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal-backdrop fade show"
      role="button"
      tabindex="0"
      @click="!loading && handleClose()"
      @keydown.enter="!loading && handleClose()"
      @keydown.space="!loading && handleClose()"
    />
  </Teleport>

  <BypassConfirmationModal
    v-model="showConfirmModal"
    title="Confirmar Aprovação de Instalação"
    message="Esta ação é irreversível e será registrada para auditoria.
      Deseja continuar com a aprovação manual da instalação?"
    type="warning"
    confirm-text="Sim, Aprovar"
    cancel-text="Cancelar"
    :loading="loading"
    @confirm="handleConfirmApproval"
    @cancel="handleCancelConfirmation"
  />
</template>

<style scoped lang="scss">
.modal {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1055;

  &.show {
    display: block !important;
  }

  .modal-dialog {
    z-index: 1056;
    position: relative;
  }
}

.modal-backdrop {
  z-index: 1054;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  cursor: pointer;
}

.modal-content {
  border-radius: 12px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 1.5rem;

  .modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
  }

  .btn-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6c757d;
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f8f9fa;
      color: #495057;
    }

    &::before {
      content: '×';
      font-size: 1.5rem;
      line-height: 1;
    }
  }
}

.modal-body {
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}

.modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  align-items: center;

  .btn {
    min-width: 120px;
    font-weight: 500;
    border-radius: 6px;
    padding: 0.5rem 1rem;
    transition: all 0.2s ease;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.list-group-item {
  border-radius: 8px;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.btn-ghost-danger {
  color: #dc3545;
  background: transparent;
  border: none;
  padding: 0.25rem 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(220, 53, 69, 0.1);
  }
}
</style>
